import type { ConductionResults } from '@wix/wix-petri-client';
import { allPass, always, any, complement, ifElse, isEmpty, lensProp, map, over, partition, pipe, reject } from 'ramda';
import { getAllowedEntitiesForLocale } from '../locale';
import { evaluateCondition, isConditionWithoutClientChecks, isConditionWithServerChecks } from './conditions';
import type { EntityBase } from '../../types/Entities';
import { isArrayWithItems } from '../array';

export const filterOutEntitiesList = <Entity extends EntityBase>(
  entities: Entity[] = [],
  { experiments, locale }: { experiments: ConductionResults; locale: string },
): Entity[] => {
  const isConditionPassing = evaluateCondition({ experiments });

  // if conditions are empty - consider them passing
  const evaluateConditions = ifElse(isArrayWithItems, any(isConditionPassing), always(true));

  const filteredByConditions = getAllowedEntitiesForLocale(locale, entities).filter((entity) => {
    const { conditions = [] } = entity;
    const conditionsWithServerChecks = conditions.filter(isConditionWithServerChecks);

    if (conditionsWithServerChecks.length === conditions.length) {
      // we have only server conditions - decide if we keep an entity or not the usual way
      return evaluateConditions(conditionsWithServerChecks);
    }

    // we have both client and server conditions, need to keep this entity
    return true;
  });

  return map((entity) => {
    if (!Array.isArray(entity.conditions)) {
      // entity has no conditions, which means it's allowed
      return entity;
    }

    return over(
      lensProp('conditions'),
      pipe(partition(isConditionWithoutClientChecks), ([serverOnlyConditions, _clientConditions]) => {
        const clientConditions = reject(
          // reject conditions that failed on server
          allPass([isConditionWithServerChecks, complement(isConditionPassing)]),
          _clientConditions,
        );

        if (isEmpty(serverOnlyConditions)) {
          return clientConditions;
        }

        const areServerOnlyConditionsPassing = evaluateConditions(serverOnlyConditions);

        return areServerOnlyConditionsPassing ? [] : clientConditions;
      }),
      entity,
    );
  }, filteredByConditions);
};
